import { formatPercent } from '../domain/format'
import { PASS_RATE_DEFINITION } from '../domain/aggregate'
import type { ReactNode } from 'react'
import { Icon, type IconName } from './Icon'
import { Button } from './Button'
import { GridHalvesContext, useCardVisibility } from './cardVisibility'

export function GridHalves({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <GridHalvesContext.Provider value={true}>
      <div className={`grid grid-halves ${className}`.trim()}>{children}</div>
    </GridHalvesContext.Provider>
  )
}

export function Card({
  title,
  description,
  actions,
  children,
  collapsible,
  id,
  tour,
}: {
  title?: string
  description?: string
  actions?: ReactNode
  children: ReactNode
  collapsible?: boolean
  id?: string
  /** Its name for the visual guides (src/tours.ts). */
  tour?: string
}) {
  const { canCollapse, hidden, toggleHidden } = useCardVisibility(title, id, collapsible)

  return (
    <section className={`card${hidden ? ' card-collapsed' : ''}`} id={id} data-tour={tour}>
      {(title || actions || canCollapse) && (
        <div className="card-head">
          <div>
            {title && <h2>{title}</h2>}
            {!hidden && description && <p>{description}</p>}
          </div>
          {(actions || canCollapse) && (
            <div className="row card-head-actions">
              {!hidden && actions}
              {canCollapse && (
                <Button
                  tone="quiet"
                  size="small"
                  icon={hidden ? 'chevronDown' : 'chevronUp'}
                  iconOnly
                  label={hidden ? `Show ${title ?? 'card'}` : `Hide ${title ?? 'card'}`}
                  title={hidden ? `Show ${title ?? 'card'}` : `Hide ${title ?? 'card'}`}
                  aria-expanded={!hidden}
                  onClick={toggleHidden}
                />
              )}
            </div>
          )}
        </div>
      )}
      {!hidden && <div className="card-body">{children}</div>}
    </section>
  )
}

export function Tile({
  label,
  value,
  note,
  tone,
  icon,
  onSelect,
}: {
  label: string
  value: ReactNode
  note?: ReactNode
  tone?: 'danger' | 'warn'
  icon?: ReactNode
  /** Makes the tile a button, for example to scroll to its details. */
  onSelect?: () => void
}) {
  const Tag = onSelect ? 'button' : 'div'
  return (
    <Tag
      className={`tile${tone ? ` tile-${tone}` : ''}`}
      {...(onSelect && { type: 'button', onClick: onSelect })}
    >
      <span className="row items-center justify-between tile-head">
        <span className="label">{label}</span>
        {icon}
      </span>
      <span className="value">{value}</span>
      {note !== undefined && <span className="note">{note}</span>}
    </Tag>
  )
}

/**
 * Proportions of an outcome breakdown.
 *
 * Used for the pass/fail/conflict split of a single group, where the parts
 * genuinely do make a whole. It is deliberately not used for quality
 * dimensions: indicators belong to several dimensions at once, so those groups
 * overlap and drawing them as shares of a whole would misstate the data.
 */
export function Percent({ value }: { value: number | undefined }) {
  if (value === undefined)
    return (
      <span className="faint" title="No result passed, failed or conflicted, so there is no rate">
        not rated
      </span>
    )
  return <span title={PASS_RATE_DEFINITION}>{formatPercent(value)}</span>
}

export function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warn' | 'error'
  children: ReactNode
}) {
  // An error is announced as it appears; other notices are read in place.
  return (
    <div
      className={`notice${tone === 'info' ? '' : ` notice-${tone}`}`}
      role={tone === 'error' ? 'alert' : undefined}
    >
      {children}
    </div>
  )
}

/** What a page or list shows when it has nothing to show, and what to do about it. */
export function EmptyState({
  icon,
  title,
  children,
  actions,
  level = 2,
}: {
  icon?: IconName
  title: ReactNode
  /** The heading's level, to fit the outline it sits in; a page's own is 2. */
  level?: 2 | 3 | 4
  children?: ReactNode
  actions?: ReactNode
}) {
  const Heading = `h${level}` as const
  return (
    <div className="empty-state-box">
      {icon && <Icon name={icon} size={32} />}
      <Heading className="empty-state-title">{title}</Heading>
      {children && <p className="muted">{children}</p>}
      {actions && <div className="cluster mt-2">{actions}</div>}
    </div>
  )
}
